import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Menu, RefreshCw, LogOut } from "lucide-react";
import { syncSmartsheet } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { canSee } from "@/lib/access";
import { navFor, isActiveNav, openNav } from "@/lib/navigation";
import { Button } from "@/components/ui/button";
import { MobileNavSheet } from "@/components/pipeline/PipelineShell";
import { useBrand } from "@/lib/brand";

// Smartsheet import is Bad Ass's own integration; other companies never see it.
const useSmartsheet = (user) => { const b = useBrand(); return !!b?.primary && canSee(user, "horse_ads"); };

// Horse Ads top bar, page mobile menu and bottom tab bar — all read the shared lib/navigation.js list.
export const openSellerDashboard = (path = "/dashboard") => window.open(`${window.location.origin}${path}`, "_blank", "noopener");

const useResync = (onSynced, after) => {
  const [syncing, setSyncing] = useState(false);
  const resync = async () => {
    setSyncing(true);
    try {
      const res = await syncSmartsheet();
      toast.success(`Synced from Smartsheet — ${res.created} new, ${res.updated} updated`);
      after?.();
      onSynced?.();
    } catch {
      toast.error("Smartsheet sync failed");
    } finally {
      setSyncing(false);
    }
  };
  return [syncing, resync];
};

const iconBtn = "h-10 w-10 inline-flex items-center justify-center rounded-full border border-white/20 text-white/85 hover:text-white hover:bg-white/10 transition-colors";

export function DesktopNav({ onSynced }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout, user } = useAuth();
  const [syncing, resync] = useResync(onSynced);
  const items = navFor(user);
  const smartsheet = useSmartsheet(user);
  return (
    <div data-testid="desktop-nav" className="hidden lg:flex items-center gap-1 xl:gap-2">
      <nav className="flex items-center gap-0.5 xl:gap-1 mr-1 xl:mr-3">
        {items.filter((n) => n.top === "primary").map((n) => {
          const on = isActiveNav(n, pathname);
          const Icon = n.icon;
          return (
            <button key={n.id} type="button" data-testid={n.topTestId} onClick={() => openNav(n, navigate)} title={n.label} aria-label={n.label}
              aria-current={on ? "page" : undefined}
              className={`relative h-10 px-2.5 xl:px-3 inline-flex items-center gap-2 rounded-full text-[11px] uppercase tracking-[0.16em] font-semibold transition-colors ${on ? "text-white" : "text-white/70 hover:text-white"}`}>
              <Icon className="h-4 w-4 xl:hidden" />
              <span className="hidden xl:inline whitespace-nowrap">{n.label}</span>
              {on && <span aria-hidden className="absolute left-3 right-3 -bottom-[13px] h-0.5 bg-snow hidden xl:block" />}
            </button>
          );
        })}
      </nav>
      {items.filter((n) => n.top === "tool").map((n) => {
        const Icon = n.icon;
        return (
          <button key={n.id} type="button" data-testid={n.topTestId} title={n.hint || n.label} aria-label={n.hint || n.label}
            onClick={() => openNav(n, navigate)}
            className={`${iconBtn} ${isActiveNav(n, pathname) ? "!border-white/60 !text-white bg-white/10" : ""}`}>
            <Icon className="h-4 w-4" />
          </button>
        );
      })}
      {smartsheet && <Button data-testid="resync-smartsheet-button" onClick={resync} disabled={syncing} title="Resync Smartsheet"
        className="ml-1 h-10 rounded-full bg-stone-600 hover:bg-stone-500 text-white px-3 xl:px-4 font-semibold">
        <RefreshCw className={`h-4 w-4 xl:mr-2 ${syncing ? "animate-spin" : ""}`} />
        <span className="hidden xl:inline">{syncing ? "Syncing…" : "Resync"}</span>
      </Button>}
      <button type="button" data-testid="admin-logout-button" onClick={logout} title="Log out" aria-label="Log out" className={iconBtn}>
        <LogOut className="h-4 w-4" />
      </button>
    </div>
  );
}

export function MobileMenu({ onSynced }) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const [syncing, resync] = useResync(onSynced, () => setOpen(false));
  const smartsheet = useSmartsheet(user);
  return (
    <>
      <Button data-testid="mobile-menu-button" variant="outline" size="icon" onClick={() => setOpen(true)}
        className="lg:hidden h-10 w-10 shrink-0 rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 hover:text-white" aria-label="Open menu">
        <Menu className="h-5 w-5" />
      </Button>
      <MobileNavSheet open={open} onOpenChange={setOpen} footer={smartsheet && (
        <Button data-testid="menu-resync" onClick={resync} disabled={syncing} className="w-full h-12 rounded-full bg-stone-600 hover:bg-stone-500 text-white font-semibold">
          <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />{syncing ? "Syncing…" : "Resync Smartsheet"}
        </Button>
      )} />
    </>
  );
}

export function MobileTabBar() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuth();
  const tabs = navFor(user).filter((n) => n.tab);
  return (
    <nav data-testid="mobile-tab-bar" style={{ gridTemplateColumns: `repeat(${tabs.length || 1}, minmax(0, 1fr))` }}
      className="md:hidden fixed bottom-0 inset-x-0 z-50 bg-ink border-t border-white/10 grid pb-[env(safe-area-inset-bottom)]">
      {tabs.map((n) => {
        const active = isActiveNav(n, pathname);
        const Icon = n.icon;
        return (
          <button key={n.id} data-testid={`tab-${n.tabId || n.id}`} onClick={() => openNav(n, navigate)} aria-current={active ? "page" : undefined}
            className={`relative flex flex-col items-center justify-center gap-1 min-h-[58px] text-[10px] uppercase tracking-[0.14em] font-semibold transition-colors ${active ? "text-white" : "text-white/65"}`}>
            {active && <span aria-hidden className="absolute top-0 h-0.5 w-8 rounded-full bg-snow" />}
            <Icon className="h-5 w-5" strokeWidth={active ? 2.2 : 1.8} /> {n.tab}
          </button>
        );
      })}
    </nav>
  );
}
